/**
 * Internationalisation.
 *
 * The product ships in Turkish and English; the infrastructure is built so a
 * third language is a file plus one registry entry, with no component changes.
 *
 * Design notes:
 *
 *   - English is the reference locale and defines the message contract. Other
 *     locales are typed against it, so an untranslated key fails the build
 *     instead of rendering blank in front of a customer.
 *   - Locales load eagerly. The whole message set is a few kilobytes, far less
 *     than the request that lazy loading would cost, and it avoids a flash of
 *     missing text on first paint.
 *   - Messages with arguments are functions. Grammatical agreement differs per
 *     language — English pluralises after a numeral, Turkish does not — and a
 *     template format cannot express that.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

import { en, type Messages } from './locales/en';
import { tr } from './locales/tr';

/** Every locale the product ships. Adding one means adding it here. */
export const locales = { tr, en } as const;

export type Locale = keyof typeof locales;

export const DEFAULT_LOCALE: Locale = 'tr';

const STORAGE_KEY = 'locale';

function isLocale(value: unknown): value is Locale {
    return typeof value === 'string' && value in locales;
}

/**
 * Resolves the locale to use: an explicit choice wins, then the browser's
 * preference, then the default.
 */
export function detectLocale(): Locale {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (isLocale(stored)) return stored;
    } catch {
        // Private browsing can block storage; fall through to the browser.
    }
    for (const tag of navigator.languages ?? [navigator.language]) {
        const base = tag.split('-')[0];
        if (isLocale(base)) return base;
    }
    return DEFAULT_LOCALE;
}

interface I18n {
    locale: Locale;
    /** The active message set. */
    t: Messages;
    setLocale: (locale: Locale) => void;
    /** Locale-aware number formatting, memoised per locale. */
    formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
    /** Currency formatting; the marketplace domain is full of money. */
    formatCurrency: (value: number, currency: string) => string;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState<Locale>(detectLocale);
    const messages = locales[locale];

    // Keeps assistive technology and the browser's own features (translation
    // offers, hyphenation, spellcheck) in step with the rendered language.
    useEffect(() => {
        document.documentElement.lang = messages.meta.htmlLang;
    }, [messages]);

    const setLocale = useCallback((next: Locale) => {
        setLocaleState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch {
            // The choice still applies for this session.
        }
    }, []);

    const tag = messages.meta.htmlLang;

    const formatNumber = useCallback((value: number, options?: Intl.NumberFormatOptions) => new Intl.NumberFormat(tag, options).format(value), [tag]);

    const formatCurrency = useCallback(
        (value: number, currency: string) => new Intl.NumberFormat(tag, { style: 'currency', currency }).format(value),
        [tag],
    );

    return <I18nContext.Provider value={{ locale, t: messages, setLocale, formatNumber, formatCurrency }}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
    const value = useContext(I18nContext);
    if (!value) {
        throw new Error('useI18n must be used inside I18nProvider');
    }
    return value;
}

/** Shorthand for the common case of only needing the messages. */
export function useMessages(): Messages {
    return useI18n().t;
}

export { en };
export type { Messages };
